import type { JSX } from 'react';
import { useEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CameraOutlined,
  CloseOutlined,
  EyeOutlined,
  FileOutlined,
  LayoutOutlined,
  PushpinOutlined,
  SendOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';

import { Button, Tag } from '../ds';
import { friendlyError } from '../../lib/api';
import { authApi } from '../../lib/authApi';
import {
  OPENING_TASKS_KEYS,
  openingTasksApi,
  type MomentDto,
  type RoomThreadDto,
  type ThreadLimits,
  type ThreadMessageDto,
} from '../../lib/api/openingTasks';
import { nameClipboardFiles, pickPastedFiles } from './clipboard-files';
import { MESSAGE_MAX_CHARS, composerEnterAction, isMacPlatform, messageLengthState } from './composer-keys';
import { MessageFiles } from './ThreadFiles';
import { bubbleSide, onBehalfNote, viewAsComposerNote } from './thread-messages';
import { fmtDuration, fmtSize, fmtWhen, initials, KIND_LABEL, prepareFiles, type PendingFile } from './utils';

/** Переписка с куратором по помещению: сообщения + поле ответа с вложениями. */
export function RoomChat({
  roomId,
  thread,
  rejected,
  flashMsgId,
  onOpenPhoto,
}: {
  roomId: string;
  thread: RoomThreadDto;
  rejected: boolean;
  /** Сообщение, к которому перешли из «Моментов» — прокрутить и подсветить. */
  flashMsgId: string | null;
  onOpenPhoto: (fileId: string) => void;
}): JSX.Element {
  const listRef = useRef<HTMLDivElement>(null);
  // «Смотреть от лица партнёра»: менеджер пишет «от имени партнёра» (владелец 19.10.2026) —
  // та же форма с теми же кнопками и лимитами, над ней плашка, чьим именем уйдёт сообщение.
  const me = useQuery({ queryKey: ['wiki-auth', 'me'], queryFn: () => authApi.me(), retry: false, staleTime: 60_000 }).data;
  const viewAs = me?.viewAs ?? null;
  const momentsById = new Map(thread.moments.map((m) => [m.id, m] as const));
  const count = thread.messages.length;

  // Новое сообщение / первая загрузка — к концу ленты (на телефоне лента без
  // своей прокрутки, там это ни на что не влияет).
  useEffect(() => {
    if (flashMsgId) return;
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [count, flashMsgId]);

  useEffect(() => {
    if (!flashMsgId) return;
    document.getElementById(`fk-ot-msg-${flashMsgId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [flashMsgId]);

  return (
    <>
      {count > 0 ? (
        <div className="fk-ot-thread" ref={listRef}>
          {thread.messages.map((m) => (
            <MessageItem
              key={m.id}
              m={m}
              moment={m.momentId ? momentsById.get(m.momentId) ?? null : null}
              flash={m.id === flashMsgId}
              onOpenPhoto={onOpenPhoto}
            />
          ))}
        </div>
      ) : (
        <div className="fk-ot-note">Сообщений пока нет. Напишите куратору, приложите фото, видео обхода или планировку.</div>
      )}
      {rejected ? (
        <div className="fk-ot-note">
          Помещение не подошло — переписка сохранена. Новое помещение — кнопка «Отправить новое помещение» выше.
        </div>
      ) : (
        <>
          {viewAs ? <ViewAsNote realName={viewAs.realName} /> : null}
          <Composer roomId={roomId} limits={thread.limits} onBehalf={Boolean(viewAs)} />
        </>
      )}
    </>
  );
}

/**
 * Над формой ответа в режиме «смотреть от лица партнёра»: сообщение и файлы уйдут
 * от имени партнёра (автор — настоящий пользователь, сервер проверяет его право
 * управлять заявками партнёров), партнёр увидит подпись «Добавил менеджер …».
 */
function ViewAsNote({ realName }: { realName: string }): JSX.Element {
  return (
    <div className="fk-ot-callout" data-tone="info" role="note">
      <EyeOutlined />
      <div style={{ minWidth: 0 }}>{viewAsComposerNote(realName)}</div>
    </div>
  );
}

function MessageItem({
  m,
  moment,
  flash,
  onOpenPhoto,
}: {
  m: ThreadMessageDto;
  moment: MomentDto | null;
  flash: boolean;
  onOpenPhoto: (fileId: string) => void;
}): JSX.Element {
  const id = `fk-ot-msg-${m.id}`;
  if (m.kind === 'system' || m.author.side === 'system') {
    return (
      <div className="fk-ot-sys" id={id}>
        {m.body}
        <time dateTime={m.createdAt}>· {fmtWhen(m.createdAt)}</time>
        {m.files.length ? (
          <div style={{ marginTop: 6 }}>
            <MessageFiles files={m.files} onOpenPhoto={onOpenPhoto} />
          </div>
        ) : null}
      </div>
    );
  }
  // «От имени партнёра» — сообщение менеджера на стороне партнёра; подпись говорит, кто его добавил.
  const side = bubbleSide(m);
  const behalfNote = onBehalfNote(m);
  const momentColor = moment?.status === 'confirmed' ? 'green' : moment?.status === 'done' ? 'gold' : 'orange';
  return (
    <div className="fk-ot-msg" id={id} data-side={side} data-flash={flash}>
      <span className="fk-ot-avatar" data-side={side} aria-hidden="true">
        {initials(m.author.name)}
      </span>
      <div className="fk-ot-bubble">
        <div className="fk-ot-who">
          {behalfNote ? (
            <span style={{ color: 'var(--fk-text)', fontWeight: 600, fontSize: 13 }}>{behalfNote}</span>
          ) : (
            <>
              <b>{m.author.name}</b>
              {m.mine ? <span>· вы</span> : null}
            </>
          )}
          <time dateTime={m.createdAt}>{fmtWhen(m.createdAt)}</time>
        </div>
        {m.momentId ? (
          <Tag color={momentColor} icon={<PushpinOutlined />} style={{ width: 'fit-content', maxWidth: '100%', whiteSpace: 'normal' }}>
            {moment ? `Момент: ${moment.text}` : 'Момент'}
          </Tag>
        ) : null}
        {m.body ? <p className="fk-ot-text">{m.body}</p> : null}
        <MessageFiles files={m.files} onOpenPhoto={onOpenPhoto} />
      </div>
    </div>
  );
}

/**
 * Поле ответа: текст + фото/видео/планировка. Лимиты — с сервера
 * (`thread.limits`): до 10 файлов, фото ≤ 20 МБ, планировка ≤ 50 МБ, видео
 * ≤ 500 МБ и ≤ 5 минут (длительность читаем из файла до отправки).
 */
const IS_MAC = isMacPlatform(typeof navigator === 'undefined' ? undefined : navigator);
/** Компьютер — Enter отправляет; телефон/планшет без мыши — Enter переносит строку, отправка кнопкой. */
const ENTER_SENDS = typeof window === 'undefined' || !window.matchMedia?.('(hover: none) and (pointer: coarse)').matches;

/** Вставка в место курсора так, чтобы работала отмена (Ctrl+Z / ⌘Z); React узнаёт о ней через обычный input. */
function insertAtCursor(el: HTMLTextAreaElement, s: string): void {
  el.focus();
  // execCommand устарел, но только он вставляет с сохранением истории отмены; есть во всех браузерах.
  if (document.execCommand('insertText', false, s)) return;
  el.setRangeText(s, el.selectionStart, el.selectionEnd, 'end');
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

function Composer({ roomId, limits, onBehalf }: { roomId: string; limits: ThreadLimits; onBehalf: boolean }): JSX.Element {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [pending, setPending] = useState<PendingFile[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [preparing, setPreparing] = useState(false);
  const pendingRef = useRef<PendingFile[]>([]);
  pendingRef.current = pending;
  const textRef = useRef<HTMLTextAreaElement>(null);

  // Превью картинок — objectURL: отзываем при уходе со страницы/смене помещения.
  useEffect(
    () => () => {
      pendingRef.current.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
    },
    [],
  );

  const send = useMutation({
    mutationFn: (body: string) =>
      openingTasksApi.sendMessage(roomId, {
        body,
        files: pending.map((p) => p.file),
        durations: pending.map((p) => (p.kind === 'video' ? p.durationSec : null)),
      }),
    onSuccess: (msg) => {
      qc.setQueryData<RoomThreadDto>(OPENING_TASKS_KEYS.thread(roomId), (prev) =>
        prev && !prev.messages.some((x) => x.id === msg.id) ? { ...prev, messages: [...prev.messages, msg] } : prev,
      );
      pending.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
      setPending([]);
      setText('');
      setErrors([]);
      void qc.invalidateQueries({ queryKey: OPENING_TASKS_KEYS.thread(roomId) });
      void qc.invalidateQueries({ queryKey: OPENING_TASKS_KEYS.myTasks });
    },
    onError: (e) => setErrors([friendlyError(e)]),
  });

  const busy = send.isPending || preparing;

  /** Общий путь для кнопок «Фото / Видео / Планировка» и вставки из буфера: лимиты, сжатие фото, длительность видео. */
  async function addPicked(picked: File[]): Promise<void> {
    if (!picked.length) return;
    setPreparing(true);
    try {
      const { accepted, errors: errs } = await prepareFiles(picked, pendingRef.current.length, limits);
      setPending((prev) => [...prev, ...accepted]);
      setErrors(errs);
    } finally {
      setPreparing(false);
    }
  }

  async function addFiles(list: FileList | null, input: HTMLInputElement): Promise<void> {
    const picked = Array.from(list ?? []);
    input.value = '';
    await addPicked(picked);
  }

  /**
   * Ctrl+V / ⌘V / «Вставить» (владелец 19.10.2026): скриншот или скопированное фото из буфера идут
   * тем же путём, что кнопки вложений. Обычный текст не трогаем: `preventDefault` только когда в буфере
   * есть файл. Если вместе с файлом там настоящий текст (Excel/Word) — вставляем только текст, картинку не прикрепляем.
   */
  function onPaste(e: ClipboardEvent<HTMLFormElement>): void {
    const { files: pasted, swallowDefault } = pickPastedFiles(e.clipboardData);
    if (!pasted.length) return;
    if (swallowDefault) e.preventDefault();
    if (send.isPending) {
      setErrors(['Сообщение отправляется — вставьте файл после отправки.']);
      return;
    }
    void addPicked(nameClipboardFiles(pasted, pendingRef.current.map((p) => p.file.name)));
  }

  /** Enter — отправить, Ctrl/⌘/Shift+Enter — новая строка (владелец 30.10.2026). Ctrl+V на Mac — общий `lib/paste-fixes.ts`. */
  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>): void {
    const key = {
      key: e.key,
      code: e.code,
      keyCode: e.nativeEvent.keyCode,
      ctrlKey: e.ctrlKey,
      metaKey: e.metaKey,
      shiftKey: e.shiftKey,
      altKey: e.altKey,
      isComposing: e.nativeEvent.isComposing,
    };
    const action = composerEnterAction(key, { enterSends: ENTER_SENDS });
    if (action === 'newline') {
      e.preventDefault();
      insertAtCursor(e.currentTarget, '\n');
    } else if (action === 'send') {
      e.preventDefault();
      // Пустое поле без файлов — Enter молчит (без пустых строк и без ошибки «напишите сообщение»).
      if ((textRef.current?.value ?? text).trim() || pending.length) submit();
    }
  }

  function removeFile(key: string): void {
    setPending((prev) => {
      const hit = prev.find((p) => p.key === key);
      if (hit?.previewUrl) URL.revokeObjectURL(hit.previewUrl);
      return prev.filter((p) => p.key !== key);
    });
  }

  function submit(): void {
    if (busy) return;
    // Текст — из самого поля: диктовку мимо React ловит `lib/paste-fixes.ts`, это — страховка, если состояние отстало.
    const body = (textRef.current?.value ?? text).trim();
    if (!body && pending.length === 0) {
      setErrors(['Напишите сообщение или приложите файл.']);
      return;
    }
    if (body.length > MESSAGE_MAX_CHARS) {
      setErrors([`Сообщение длиннее ${MESSAGE_MAX_CHARS.toLocaleString('ru-RU')} знаков — сократите или отправьте двумя сообщениями.`]);
      return;
    }
    setErrors([]);
    send.mutate(body);
  }

  const totalBytes = pending.reduce((s, p) => s + p.file.size, 0);
  const full = pending.length >= limits.maxFiles;
  const length = messageLengthState(text.trim().length);

  return (
    <form
      className="fk-ot-composer"
      autoComplete="off"
      onPaste={onPaste}
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      {/* Без maxLength: браузер молча обрезал вставку — длинный текст виден целиком, предел — счётчиком ниже. */}
      <textarea
        ref={textRef}
        aria-label={onBehalf ? 'Сообщение от имени партнёра' : 'Ответ куратору'}
        placeholder={
          onBehalf
            ? 'Сообщение от имени партнёра. Например: собственник согласен на выход вытяжки на кровлю, письмо прикладываю'
            : 'Ответ куратору. Например: собственник согласен на выход вытяжки на кровлю, письмо прикладываю'
        }
        value={text}
        disabled={send.isPending}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
      />
      {length.show ? (
        <div className="fk-ot-hint" style={{ textAlign: 'right', padding: '0 12px 4px', color: length.over ? 'var(--fk-error)' : undefined }}>
          {length.label}
          {length.over ? ' — длиннее, чем можно отправить' : ''}
        </div>
      ) : null}
      {pending.length ? (
        <div className="fk-ot-pending">
          {pending.map((p) => (
            <span className="fk-ot-pend" key={p.key}>
              {p.previewUrl ? (
                <img src={p.previewUrl} alt="" />
              ) : (
                <span className="fk-ot-pend-ic" aria-hidden="true">
                  {p.kind === 'video' ? <VideoCameraOutlined /> : p.kind === 'plan' ? <LayoutOutlined /> : <FileOutlined />}
                </span>
              )}
              <span className="fk-ot-pend-name" title={p.file.name}>
                {KIND_LABEL[p.kind]}: {p.file.name}
              </span>
              <small>
                {fmtSize(p.file.size)}
                {p.kind === 'video' && p.durationSec != null && p.durationSec > 0 ? ` · ${fmtDuration(p.durationSec)}` : ''}
              </small>
              <button type="button" aria-label={`Убрать ${p.file.name}`} disabled={send.isPending} onClick={() => removeFile(p.key)}>
                <CloseOutlined />
              </button>
            </span>
          ))}
        </div>
      ) : null}
      {errors.length ? (
        <ul className="fk-ot-errors" role="alert" style={{ margin: '0 12px 8px' }}>
          {errors.map((e, i) => (
            <li key={i}>{e}</li>
          ))}
        </ul>
      ) : null}
      <div className="fk-ot-tools">
        <AttachButton icon={<CameraOutlined />} label="Фото" accept="image/*" disabled={busy || full} onPick={addFiles} />
        <AttachButton icon={<VideoCameraOutlined />} label="Видео" accept="video/*" disabled={busy || full} onPick={addFiles} />
        <AttachButton icon={<LayoutOutlined />} label="Планировка" accept=".pdf,.dwg,.dxf,image/*" disabled={busy || full} onPick={addFiles} />
        <span className="fk-ot-tools-sp" />
        {send.isPending && totalBytes > 5 * 1024 * 1024 ? (
          <span className="fk-ot-hint">Загружаем {fmtSize(totalBytes)} — не закрывайте страницу</span>
        ) : preparing ? (
          <span className="fk-ot-hint">Проверяем файлы…</span>
        ) : (
          <span className="fk-ot-hint">
            {pending.length}/{limits.maxFiles} файлов · видео до {Math.round(limits.maxVideoSec / 60)} мин
            <span className="fk-ot-paste-hint">
              {' '}
              · скриншот можно вставить ({IS_MAC ? '⌘V' : 'Ctrl+V'}){ENTER_SENDS ? ' · Enter — отправить, Ctrl+Enter — новая строка' : ''}
            </span>
          </span>
        )}
        <Button type="submit" variant="primary" size="sm" icon={<SendOutlined />} loading={send.isPending} disabled={preparing}>
          Отправить
        </Button>
      </div>
    </form>
  );
}

function AttachButton({
  icon,
  label,
  accept,
  disabled,
  onPick,
}: {
  icon: JSX.Element;
  label: string;
  accept: string;
  disabled: boolean;
  onPick: (files: FileList | null, input: HTMLInputElement) => void | Promise<void>;
}): JSX.Element {
  return (
    <label className="fk-ot-attach" data-disabled={disabled}>
      {icon}
      {label}
      <input
        type="file"
        accept={accept}
        multiple
        disabled={disabled}
        onChange={(e) => {
          void onPick(e.target.files, e.target);
        }}
      />
    </label>
  );
}
